class MasonryInfiniteScroll {
  constructor(collection) {
    this.collection = collection;
    this.grid = collection.querySelector('#product-grid');
    this.sentinel = collection.querySelector('[data-masonry-sentinel]');
    this.status = collection.querySelector('.masonry-infinite-scroll__status');
    this.loading = false;
    this.observer = null;
    this.resizeObserver = null;
    this.layoutFrame = null;

    if (!this.grid || !this.sentinel) return;

    this.collection.masonryInfiniteScroll = this;
    this.nextUrl = this.sentinel.dataset.nextUrl;
    this.setStatus();
    this.bindLayoutEvents();
    this.layout();
    this.observe();
  }

  bindLayoutEvents() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.queueLayout());
      this.grid.querySelectorAll('.grid__item').forEach((item) => this.resizeObserver.observe(item));
    }

    this.grid.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => this.queueLayout(), { once: true });
      image.addEventListener('error', () => this.queueLayout(), { once: true });
    });

    window.addEventListener('resize', () => this.queueLayout(), { passive: true });
  }

  watchItems(items) {
    if (this.resizeObserver) {
      items.forEach((item) => this.resizeObserver.observe(item));
    }

    items.forEach((item) => {
      item.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', () => this.queueLayout(), { once: true });
        image.addEventListener('error', () => this.queueLayout(), { once: true });
      });
    });
  }

  queueLayout() {
    if (this.layoutFrame) return;

    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = null;
      this.layout();
    });
  }

  layout() {
    const rowHeight = parseFloat(getComputedStyle(this.grid).gridAutoRows);
    const rowGap = parseFloat(getComputedStyle(this.grid).rowGap);

    if (!rowHeight) return;

    this.grid.querySelectorAll('.grid__item').forEach((item) => {
      item.style.gridRowEnd = '';
      const itemHeight = item.getBoundingClientRect().height;
      const span = Math.ceil((itemHeight + rowGap) / (rowHeight + rowGap));
      item.style.gridRowEnd = `span ${span}`;
    });
  }

  observe() {
    if (!this.nextUrl) return;

    if (!('IntersectionObserver' in window)) {
      this.loadNextPage();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNextPage();
        }
      },
      { rootMargin: '800px 0px' }
    );

    this.observer.observe(this.sentinel);
  }

  setStatus(message) {
    if (!this.status) return;

    if (message) {
      this.status.textContent = message;
      return;
    }

    this.status.textContent = this.nextUrl ? 'Loading more products' : 'All products loaded';
    this.sentinel.classList.toggle('masonry-infinite-scroll--complete', !this.nextUrl);
  }

  buildSectionUrl() {
    const url = new URL(this.nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.grid.dataset.id);
    return url.toString();
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.sentinel.classList.add('is-loading');
    this.setStatus('Loading more products');

    try {
      const response = await fetch(this.buildSectionUrl());
      if (!response.ok) throw new Error(response.status);

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = parsedDocument.querySelector('#product-grid');
      const nextSentinel = parsedDocument.querySelector('[data-masonry-sentinel]');

      if (nextGrid) {
        const newItems = Array.from(nextGrid.querySelectorAll('.grid__item'));

        newItems.forEach((item) => {
          this.grid.appendChild(item);
        });

        this.watchItems(newItems);
        this.queueLayout();
      }

      this.nextUrl = nextSentinel ? nextSentinel.dataset.nextUrl : '';
      this.sentinel.dataset.nextUrl = this.nextUrl || '';
      this.setStatus();
    } catch (error) {
      this.setStatus('More products could not be loaded');
    } finally {
      this.loading = false;
      this.sentinel.classList.remove('is-loading');

      if (!this.nextUrl && this.observer) {
        this.observer.disconnect();
      }
    }
  }
}

function initMasonryInfiniteScroll(context = document) {
  context.querySelectorAll('[data-masonry-collection]').forEach((collection) => {
    if (collection.masonryInfiniteScroll) return;
    new MasonryInfiniteScroll(collection);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initMasonryInfiniteScroll();

  const productGridContainer = document.getElementById('ProductGridContainer');
  if (!productGridContainer) return;

  new MutationObserver(() => {
    initMasonryInfiniteScroll(productGridContainer);
  }).observe(productGridContainer, { childList: true });
});
